import { onBeforeUnmount, onMounted } from 'vue'
import { usePipe, PipeDisplayMode } from '@render/composables/usePipe'

// On every real route transition in a built pipe, reset the visitor's view so the
// new page opens at the top-left origin. Two parts, both needed:
//
//  - `window.scrollTo(0, 0)` resets the layout scroll (the common, un-zoomed case).
//  - Toggling the viewport meta (clamp `maximum-scale` down to the page's fit scale +
//    `user-scalable=no`, then restore) forces the browser to recompute the viewport.
//    This is what snaps a pinch-zoomed / panned view back to the fit origin —
//    `window.scrollTo` only moves the layout scroll, not the zoomed visual viewport, so
//    on its own it leaves a zoomed page parked on whatever region the visitor panned to.
//    The clamp target is the FIT scale (device width / measured layout-viewport width),
//    not 1.0, because on a device narrower than the layout the fresh-load scale is
//    already < 1 — clamping to 1.0 would only reset part-way. The fit is measured from
//    the DOM, not derived from md-container or the `.body-content` zoom rule, so it holds
//    regardless of how the page achieves its fit (see resetView). The meta toggle
//    does the visible work here; the JS `visualViewport.scale` reading can stay stale
//    (notably under Chrome DevTools device emulation) even though the view visibly
//    resets, so don't trust that number — judge it on a real device.
//
// We also READ `visualViewport.scale` up front as a guard: the toggle only runs when the
// visitor is actually pinch-zoomed in (`scale > 1`). At the fit origin `scale` is exactly
// 1.0, so the toggle is skipped — clamping `maximum-scale` + `user-scalable=no` while
// already at fit makes Android re-fit and visibly zoom the page IN, which is wrong on an
// un-zoomed navigation (the common case). The same DevTools-emulation staleness applies to
// this read: under emulation the guard may skip a needed reset, so verify zoom-out on a
// real device. The scroll/position reset above is unconditional and unaffected.
//
// We hook `navigation:leave` (fired at the start of the route watcher in
// [...slug].vue, before the new page renders and before `useAutoFocus`'s
// `navigation:show` handler) so our restore is scheduled before autofocus reads the
// viewport — autofocus never captures our transient lock. `navigation:leave` is also
// editor-gated at the firing site, so the editor preview never resets.
export function useResetViewOnNavigation(): void {
  const pipe = usePipe()
  let unsub: (() => void) | undefined

  // Capture the canonical (build-time, zoomable) viewport once, before any runtime
  // mutation. Reading it live at reset time would break the soft-keyboard edge case:
  // if an input is focused, `useAutoFocus` has already locked `maximum-scale=1.0`, so
  // a live read would "restore" to the locked value and disable zoom permanently.
  const canonical =
    typeof document !== 'undefined'
      ? (document.querySelector('meta[name="viewport"]')?.getAttribute('content') ?? '')
      : ''

  function resetView() {
    if (typeof window === 'undefined') return
    if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return

    // Reset the layout scroll — both the window and the scrolling element so it works
    // regardless of which one actually scrolls.
    window.scrollTo(0, 0)
    const se = document.scrollingElement as HTMLElement | null
    if (se) se.scrollTop = 0

    // Only touch the viewport meta when the visitor has actually pinch-zoomed IN past the
    // page's fit origin. `visualViewport.scale` is 1.0 at the fresh-load fit and grows
    // when pinched in. When already at fit there's nothing to reset, and clamping
    // `maximum-scale` + `user-scalable=no` makes Android re-fit and visibly zoom the page
    // IN — so skip the toggle entirely. `window.scrollTo` above still runs every
    // navigation, so the iOS position reset is unaffected. When visualViewport is
    // unavailable we can't tell, so fall through to the legacy toggle.
    const vv = window.visualViewport
    if (vv && vv.scale <= 1.0001) return

    // Snap a zoomed/panned view back to the fit origin via a viewport-meta toggle:
    // momentarily clamp `maximum-scale` down to the page's natural fit scale, then
    // restore the zoomable viewport. Skip when the meta is missing/malformed so we
    // never write a broken value.
    if (!canonical || !/maximum-scale\s*=/.test(canonical)) return
    const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
    if (!viewport) return

    // A fresh load sits at the browser's shrink-to-fit scale, which is < 1 whenever the
    // device is narrower than the layout viewport — so clamping to `maximum-scale=1`
    // resets a pinch only part-way (it stops at 1, still larger than the fit). Clamp to
    // the actual fit scale instead. We MEASURE it rather than replicate any specific fit
    // mechanism (viewport shrink-to-fit, the `.body-content { zoom: var(--app-zoom) }`
    // rule, a user-edited md-container, future baseCss, …):
    // the fit is the device width over the live layout-viewport width, both read from
    // the DOM. `clientWidth` is the layout viewport, unaffected by pinch-zoom or any CSS
    // `zoom`, so this tracks whatever the page actually does.
    const layoutWidth = document.documentElement.clientWidth
    const deviceWidth = window.screen?.width || window.innerWidth || layoutWidth
    const fitScale = layoutWidth > 0 ? Math.min(deviceWidth / layoutWidth, 1) : 1
    const locked = `${canonical.replace(
      /maximum-scale\s*=\s*[^,]+/,
      `maximum-scale=${fitScale.toFixed(4)}`,
    )}, user-scalable=no`
    viewport.setAttribute('content', locked)
    // Restore the zoomable viewport shortly after so the visitor can pinch-zoom the
    // new page; the view stays at the fit origin the toggle reset it to.
    setTimeout(() => viewport.setAttribute('content', canonical), 300)
  }

  onMounted(() => {
    unsub = pipe.subscribe('navigation:leave', resetView)
  })

  onBeforeUnmount(() => {
    unsub?.()
  })
}
